import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = { width: '100%', padding: '7px 9px', border: `1px solid ${theme.colors.borders}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 };
const toLocal = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : '');
const toIso = (v) => (v ? new Date(v).toISOString() : null);

function initial(t) {
  const c = t?.rules?.conditions || [];
  const find = (type) => c.find(x => x.type === type);
  return {
    match: t?.rules?.match || 'all',
    claim: !!find('claim'), purchase: !!find('purchase'),
    nft: !!find('nft_ownership'), token_id: find('nft_ownership')?.token_id ?? '',
    date: !!find('date'), date_at: toLocal(find('date')?.at),
    start_at: toLocal(t?.start_at), expires_at: toLocal(t?.expires_at),
    total_limit: t?.total_limit ?? '', per_holder_limit: t?.per_holder_limit ?? '',
    transfer_behavior: t?.transfer_behavior || 'stays_with_holder',
    category: t?.category || '', fulfillment_party: t?.fulfillment_party || '',
    fulfillment_instructions: t?.fulfillment_instructions || '',
    display_title: t?.display_title || '', display_description: t?.display_description || '',
    surprise: !!t?.surprise?.enabled, teaser: t?.surprise?.teaser || '',
  };
}

export default function UtilityRuleEditor({ portfolioId, utility, published, onSaved }) {
  const [f, setF] = useState(() => initial(utility.terms));
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const frozen = published && !!utility.terms;

  const save = async () => {
    const conditions = [];
    if (f.claim) conditions.push({ type: 'claim' });
    if (f.purchase) conditions.push({ type: 'purchase' });
    if (f.nft) conditions.push({ type: 'nft_ownership', token_id: f.token_id === '' ? null : Number(f.token_id) });
    if (f.date) conditions.push({ type: 'date', at: toIso(f.date_at) });
    const body = {
      rules: { match: f.match, conditions },
      start_at: toIso(f.start_at), expires_at: toIso(f.expires_at),
      total_limit: f.total_limit === '' ? null : Number(f.total_limit),
      per_holder_limit: f.per_holder_limit === '' ? null : Number(f.per_holder_limit),
      transfer_behavior: f.transfer_behavior, category: f.category, fulfillment_party: f.fulfillment_party,
      fulfillment_instructions: f.fulfillment_instructions, display_title: f.display_title,
      display_description: f.display_description, surprise: { enabled: f.surprise, teaser: f.teaser },
    };
    setSaving(true); setErrors([]);
    try {
      await axios.put(`${API}/creator/portfolios/${portfolioId}/utility-rules/${encodeURIComponent(utility.association_key)}`, body, auth);
      toast.success('Unlock rules saved');
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErrors(typeof d === 'string' ? [d] : d?.errors || ['Save failed']);
    } finally { setSaving(false); }
  };

  const check = (k, label, hint) => (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer' }}>
      <input data-testid={`rule-cond-${k}`} type="checkbox" checked={f[k]} onChange={set(k)} style={{ marginTop: 3 }} />
      <span><b>{label}</b><br /><span style={{ fontSize: 11, color: theme.colors.text.muted }}>{hint}</span></span>
    </label>
  );

  return (
    <div data-testid="utility-rule-editor" style={{ marginTop: 12, padding: 14, background: '#fff', borderRadius: 8, border: `1px solid ${theme.colors.borders}` }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Unlocks when the Holder…</div>
      <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
        {check('claim', 'Claims this Kickback', 'Account-based. No wallet or NFT needed.')}
        {check('purchase', 'Makes a recorded purchase', 'Uses purchases recorded by the platform.')}
        {check('nft', 'Owns this Kickback\'s NFT', 'Checked on-chain in a wallet the Holder has verified.')}
        {f.nft && <input data-testid="rule-token-id" style={{ ...inp, maxWidth: 220, marginLeft: 24 }} placeholder="Specific token ID (optional)" value={f.token_id} onChange={set('token_id')} />}
        {check('date', 'Reaches a date', 'Unlocks automatically at the chosen time.')}
        {f.date && <input data-testid="rule-date-at" type="datetime-local" style={{ ...inp, maxWidth: 260, marginLeft: 24 }} value={f.date_at} onChange={set('date_at')} />}
        <div style={{ fontSize: 12, color: theme.colors.text.muted }}>Milestone unlocks: not available yet — there's no reliable way to verify milestones automatically.</div>
      </div>
      <div style={{ marginBottom: 10, fontSize: 13 }}>
        <label style={lbl}>When several are ticked</label>
        <select data-testid="rule-match" style={{ ...inp, maxWidth: 320 }} value={f.match} onChange={set('match')}>
          <option value="all">Holder must meet all of them</option>
          <option value="any">Meeting any one is enough</option>
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 10 }}>
        <div><label style={lbl}>Available from</label><input data-testid="rule-start" type="datetime-local" style={inp} value={f.start_at} onChange={set('start_at')} /></div>
        <div><label style={lbl}>Ends</label><input data-testid="rule-expires" type="datetime-local" style={inp} value={f.expires_at} onChange={set('expires_at')} /></div>
        <div><label style={lbl}>Total redemptions (blank = no limit)</label><input data-testid="rule-total-limit" type="number" min="1" style={inp} value={f.total_limit} onChange={set('total_limit')} /></div>
        <div><label style={lbl}>Uses per Holder (blank = no limit)</label><input data-testid="rule-per-holder" type="number" min="1" style={inp} value={f.per_holder_limit} onChange={set('per_holder_limit')} /></div>
        <div><label style={lbl}>If the NFT is transferred</label>
          <select data-testid="rule-transfer" style={inp} value={f.transfer_behavior} onChange={set('transfer_behavior')} disabled={frozen}>
            <option value="stays_with_holder">Benefit stays with the Holder</option>
            <option value="stays_with_nft">Benefit moves with the NFT</option>
            <option value="non_transferable">Not transferable</option>
          </select></div>
        <div><label style={lbl}>Category</label><input data-testid="rule-category" style={inp} value={f.category} onChange={set('category')} disabled={frozen} /></div>
        <div><label style={lbl}>Fulfilled by</label><input data-testid="rule-fulfillment-party" style={inp} value={f.fulfillment_party} onChange={set('fulfillment_party')} disabled={frozen} /></div>
      </div>
      <div style={{ marginBottom: 10 }}><label style={lbl}>How Holders use it</label>
        <textarea data-testid="rule-instructions" style={{ ...inp, minHeight: 50, fontFamily: 'inherit' }} value={f.fulfillment_instructions} onChange={set('fulfillment_instructions')} /></div>
      <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 6 }}>
        <input data-testid="rule-surprise" type="checkbox" checked={f.surprise} onChange={set('surprise')} disabled={frozen} />
        <span><b>Surprise reveal</b> — Holders see a teaser until it unlocks; the details stay hidden.</span>
      </label>
      {f.surprise && <input data-testid="rule-teaser" style={{ ...inp, marginBottom: 10 }} placeholder="Teaser shown before reveal" value={f.teaser} onChange={set('teaser')} disabled={frozen} />}
      {frozen && <div style={{ fontSize: 11, color: '#92400E', marginBottom: 8 }}>Greyed-out fields are part of the published promise and can't change.</div>}
      {errors.length > 0 && (
        <div data-testid="utility-rule-errors" style={{ padding: 10, background: '#FEF2F2', color: '#991B1B', borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
          {errors.map((e, i) => <div key={i}>• {e}</div>)}
        </div>
      )}
      <button data-testid="utility-rule-save" onClick={save} disabled={saving}
        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: theme.colors.status.success, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
        {saving ? 'Saving…' : 'Save rules'}
      </button>
    </div>
  );
}
